export const HISTORY_LEDGER_KEY = 'primum.historyLedger';

type Ledger = { ids: string[]; position: number };

type LedgerStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

/**
 * Remembers, for one browser tab, the order of the history entries the app has stood on, so Back
 * can tell whether the entry before the current one is ours.
 *
 * The router stamps every entry it creates with an id in `history.state`, and the browser keeps
 * that id across a reload and across back and forward. `canGoBack` cannot answer the question,
 * because it reads the in-memory stack a reload rebuilds from the URL alone, and `history.length`
 * counts other sites' entries too. sessionStorage has exactly the browser back stack's scope: one
 * tab, surviving a reload.
 *
 * Every failure reads as "nothing of ours behind", which makes Back go to the screen's parent
 * rather than risk leaving the app. That covers storage that throws, as in some private windows.
 */
const UNAVAILABLE_STORAGE: LedgerStorage = {
  getItem: () => null,
  setItem: () => undefined,
  removeItem: () => undefined,
};

/** The tab's sessionStorage, or a stand-in that remembers nothing where reaching it throws. */
export function browserLedgerStorage(): LedgerStorage {
  try {
    return window.sessionStorage;
  } catch {
    return UNAVAILABLE_STORAGE;
  }
}

/** The id the router stamped on the browser's current history entry. */
export function currentHistoryEntryId(): string | undefined {
  const state: unknown = window.history.state;

  if (state && typeof state === 'object' && 'id' in state && typeof state.id === 'string') {
    return state.id;
  }

  return undefined;
}

function read(storage: LedgerStorage): Ledger {
  try {
    const parsed = JSON.parse(storage.getItem(HISTORY_LEDGER_KEY) ?? 'null') as Ledger | null;

    if (parsed && Array.isArray(parsed.ids) && Number.isInteger(parsed.position)) {
      return parsed;
    }
  } catch {
    // Unreadable or unavailable storage is treated as an empty ledger.
  }

  return { ids: [], position: -1 };
}

function write(storage: LedgerStorage, ledger: Ledger) {
  try {
    storage.setItem(HISTORY_LEDGER_KEY, JSON.stringify(ledger));
  } catch {
    // A ledger that cannot be written only costs the history path; Back still reaches the parent.
  }
}

/**
 * Starts the tab's ledger over at the given entry. Called when a document is loaded by a fresh
 * navigation (a typed address, or a link from another site), where the entry before it belongs
 * to another document that may not be the app's.
 */
export function resetHistoryLedger(storage: LedgerStorage, currentId: string | undefined) {
  write(storage, currentId ? { ids: [currentId], position: 0 } : { ids: [], position: -1 });
}

/**
 * Records that the app now stands on the entry with this id. A known id moves the position to it,
 * as back and forward do. A new id is a push, which drops every entry forward of the current one,
 * the way the browser drops its forward history.
 *
 * An id never seen before but reached by back or forward (`traversal`) is not a push: it is an
 * entry from an earlier document, and nothing says which of its neighbors are the app's. The
 * ledger starts over there, so Back from it goes to the parent rather than possibly off-site.
 */
export function recordHistoryEntry(
  storage: LedgerStorage,
  id: string | undefined,
  { traversal = false }: { traversal?: boolean } = {}
) {
  if (!id) {
    return;
  }

  const ledger = read(storage);

  const knownIndex = ledger.ids.indexOf(id);

  if (knownIndex !== -1) {
    if (knownIndex !== ledger.position) {
      write(storage, { ...ledger, position: knownIndex });
    }

    return;
  }

  if (traversal) {
    resetHistoryLedger(storage, id);

    return;
  }

  const ids = [...ledger.ids.slice(0, ledger.position + 1), id];

  write(storage, { ids, position: ids.length - 1 });
}

/** Whether the entry before the one with this id is one the app created in this tab. */
export function hasAppEntryBehind(storage: LedgerStorage, id: string | undefined): boolean {
  if (!id) {
    return false;
  }

  return read(storage).ids.indexOf(id) > 0;
}
